"use client";

import type { ReactNode } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export type AlertVariant = "success" | "danger" | "warning" | "info";

/**
 * 讀屏急迫性。alert = assertive（插播，打斷正在唸的內容）；status = polite（等唸完再補）。
 *
 * 為什麼要跟 variant 拆開：variant 是**顏色語意**（這則訊息是好是壞），
 * role 是**急迫性語意**（該不該打斷使用者）。兩者常常一致，但不是同一件事。
 * 同樣是 warning 底色：
 *   「工作階段 1 分鐘後逾時」→ 不插播使用者就會白打一整頁表單 → alert
 *   「表單有未儲存的變更」   → 常駐提醒、不影響當下操作 → status（規範 §8 表列）
 * 綁死在 variant 上時，後者只能被迫插播，或被迫改成不相稱的顏色。
 */
export type AlertRole = "alert" | "status";

export interface AlertProps {
  /** 語意。預設 info。 */
  variant?: AlertVariant;
  /** 可選標題（粗體單行）。訊息只有一句話時不必給。 */
  title?: ReactNode;
  /** 訊息本體。常駐訊息是完整句子，句尾要加句號（規範 §6-5）。 */
  children?: ReactNode;
  /** 給了才會出現關閉鈕。純資訊性的 banner 才適合關；表單送出錯誤不該可關（要留到修正為止）。 */
  onClose?: () => void;
  /**
   * 右側動作區（重試鈕、「重新整理」連結等）。
   *
   * 為什麼要有：規範 §3 判準 1「訊息包含使用者要做什麼的下一步」就不能用 Toast，
   * 那些訊息必然帶著一個動作。動作若不能跟訊息放在一起，使用者得自己找按鈕在哪。
   */
  action?: ReactNode;
  className?: string;
  /** 供 aria-describedby 等外部關聯使用。 */
  id?: string;
  /**
   * 覆寫讀屏急迫性。不給則沿用 variant 的預設對應（見 DEFAULT_ROLE）。
   *
   * 什麼時候要給：預設對應只是「多數情況」的猜測，遇到規範 §8 明列的例外就要覆寫——
   * 未儲存變更是 warning 底色但規範要求 role="status"，就在該處寫 role="status"。
   * 反向也成立：info 底色的「系統將於 5 分鐘後維護」該插播，寫 role="alert"。
   */
  role?: AlertRole;
}

/**
 * 語意圖示。與 Toast 共用同一組字元（✓／✕／!／i），全站只有一套語意形狀。
 *
 * 為什麼一定要有：只靠顏色分辨成功/失敗/警告，色弱使用者無法區分（規範 §4）。
 */
const VARIANT_ICON: Record<AlertVariant, string> = {
  success: "✓",
  danger: "✕",
  warning: "!",
  info: "i",
};

/**
 * variant 的**預設**急迫性（呼叫端可用 role prop 覆寫）。
 *
 * 判準與 Toast 一致：「使用者接下來的動作是否依賴這則訊息」——
 *   失敗/警告 → 依賴（要先知道哪裡錯了才能決定下一步）→ alert
 *   成功/資訊 → 不依賴 → status
 * 注意 Alert 與 Toast 不同的是它**常駐**，所以插播只會發生在它出現的那一刻，
 * 不會反覆干擾；反過來說，若把 danger 也寫成 status，錯誤就永遠不會被朗讀出來。
 *
 * 這裡只是預設值：規範 §8 表列的例外（未儲存變更是 warning 但要 status）
 * 由呼叫端寫 role="status" 處理，不必為了 role 去改顏色。
 */
const DEFAULT_ROLE: Record<AlertVariant, AlertRole> = {
  success: "status",
  info: "status",
  danger: "alert",
  warning: "alert",
};

/**
 * 頁內常駐訊息（Banner）。
 *
 * 與 Toast 的分工（規範 §3）
 * ------------------------
 *   Toast  = 瞬時事件，「這次操作的結果」，會自動消失，飄在右下角
 *   Alert  = 常駐狀態，「現在這一頁處於什麼狀況」，留到條件解除，貼在相關內容旁
 *
 * 用 Alert 的典型場景：表單整體送出錯誤（D）、整頁載入失敗（E）、找不到資源（F）、
 * 系統性錯誤（J）、未儲存變更（K，用 warning）。
 *
 * 這個元件的存在理由是規範 §7 缺口 4：全站 103 處 setError + 16 處手刻紅字，
 * 已經長出至少兩種不同的 inline style 寫法。有共用元件之後，「自己刻一份」
 * 在物理上等於重寫一份一樣的 CSS，成本高於直接用。
 */
export function Alert({ variant = "info", title, children, onClose, action, className, id, role }: AlertProps) {
  useInjectStyles();
  const cls = ["cui-reset", "cui-alert", `cui-alert-${variant}`, className ?? ""].filter(Boolean).join(" ");
  return (
    <div className={cls} role={role ?? DEFAULT_ROLE[variant]} id={id}>
      <span className="cui-alert-icon" aria-hidden>
        {VARIANT_ICON[variant]}
      </span>
      <div className="cui-alert-body">
        {title ? <strong className="cui-alert-title">{title}</strong> : null}
        {children ? <div className="cui-alert-msg">{children}</div> : null}
      </div>
      {action ? <div className="cui-alert-action">{action}</div> : null}
      {onClose ? (
        <button type="button" className="cui-alert-close" onClick={onClose} aria-label="關閉訊息">
          ✕
        </button>
      ) : null}
    </div>
  );
}
